import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  Grid,
  TextField,
  Button,
  Typography,
  Box,
  Card,
  IconButton,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { IconUpload, IconX, IconFileDescription } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import DownloadIcon from "@mui/icons-material/Download";
import { useQuery } from "@tanstack/react-query";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import { generatePdfThumbnail } from "utils/pdfPreview";
import { compressImage } from "utils/imageCompression";

const TicketEdit = () => {
  const { id } = useParams();
  // const location = useLocation();
  // const ticketData = location.state;
  const [preview, setPreview] = useState([]);
  const [state, setState] = useState({
    open: false,
    severity: "success",
    vertical: "top",
    horizontal: "right",
    message: "",
  });
  const [replyHistory, setReplyHistory] = useState([]);
  const [hasAdminResponded, setHasAdminResponded] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [rotate, setRotate] = useState(0);
  const [ticketStatus, setTicketStatus] = useState("");
  const [unauthorized, setUnauthorized] = useState(false);
  const [replyLoading, setReplyLoading] = useState(false);
  const navigate = useNavigate();

  const handleClose = () => setState({ ...state, open: false });

  const me = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    // console.log("🚀 ~ fetchUsers ~ response:", response);
    return response.data;
  };

  const {
    data: meData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["me"], // Unique key for the query
    queryFn: me, // The async function to run
    retry: false,
  });

  const fetchTicketById = async (ticket_id) => {
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/tickets/${ticket_id}`, {
      withCredentials: true,
    });
    return response.data.data?.[0]; // or response.data.data depending on API response
  };

  const {
    data: ticketData,
    isLoading: ticketLoading,
    isError: ticketError,
    error: ticketErrorObj,
  } = useQuery({
    queryKey: ["ticket", id],
    queryFn: () => fetchTicketById(id),
    enabled: !!id,
    retry: false,
  });
  console.log("ticketData:", ticketData);

  const formik = useFormik({
    initialValues: {
      txtContent: "",
      documents: [],
    },
    validationSchema: Yup.object({
      txtContent: Yup.string()
        .required("Ticket description is required")
        .max(500, "Maximum 500 characters allowed"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        setReplyLoading(true);
        const meRes = await axios.post(
          `${process.env.REACT_APP_API_URL}/auth/me`,
          {},
          { withCredentials: true }
        );
        const meData = meRes.data;

        const formData = new FormData();
        formData.append("ticket_id", ticketData.id);
        formData.append("created_user", ticketData.created_user);
        formData.append("created_user_district", ticketData.created_user_district);
        formData.append("ticket_description", values.txtContent);
        if (values.documents && values.documents.length > 0) {
          const compressedFiles = await Promise.all(
            values.documents.map(file => compressImage(file))
          );
          compressedFiles.forEach((file) => {
            formData.append("ticket_attachment", file);
          });
        }
        formData.append("created_by", meData.user_id);

        if ((meData.role_id === 1 || meData.role_id === 2) && !hasAdminResponded) {
          formData.append("status", "In Progress");
        }

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/tickets/edit`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            withCredentials: true,
          }
        );

        if (response.data.status === 200) {
          setState({
            ...state,
            open: true,
            severity: "success",
            message: response.data.message,
          });

          formik.resetForm();
          setPreview(null);

          setTimeout(() => {
            navigate("/tickets-list");
          }, 2000);
        }
      } catch (error) {
        console.error("Edit Ticket Error:", err);
        if (err.response) {
          console.error("Response:", err.response.data);
        }
        setState({
          ...state,
          open: true,
          severity: "error",
          message: "Failed to Edit ticket",
        });
      } finally {
        setReplyLoading(false);
      }
    },
  });

  useEffect(() => {
    const fetchReplyHistory = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/ticketsreply/${ticketData.id}`,
          {
            withCredentials: true,
          }
        );
        console.log("Fetched Reply History:", res.data.data);
        setReplyHistory(res.data.data);
        setTicketStatus(res.data.ticket_status);

        const alreadyResponded = res.data.data.some((reply) => reply.role_id === 1 || reply.role_id === 2);
        setHasAdminResponded(alreadyResponded);
      } catch (err) {
        console.error("Error fetching reply history:", err.message);
      }
    };

    if (ticketData?.id) {
      fetchReplyHistory();
    }
  }, [ticketData]);

  const handleFileChange = async (event) => {
    const files = Array.from(event.currentTarget.files);
    if (files.length > 0) {
      const newFiles = [...formik.values.documents, ...files];
      formik.setFieldValue("documents", newFiles);
      
      const previewPromises = newFiles.map(async (file) => {
        if (file.type.startsWith("image/")) {
          return { name: file.name, type: file.type, url: URL.createObjectURL(file) };
        } else if (file.type === "application/pdf") {
          try {
            const thumbnail = await generatePdfThumbnail(file);
            return { name: file.name, type: file.type, url: thumbnail };
          } catch (e) {
            return { name: file.name, type: file.type, url: null };
          }
        }
        return { name: file.name, type: file.type, url: null };
      });

      const newPreviews = await Promise.all(previewPromises);
      setPreview(newPreviews);
    }
  };
  
  const handleRemoveFile = (index) => {
    const newFiles = [...formik.values.documents];
    newFiles.splice(index, 1);
    formik.setFieldValue("documents", newFiles);
    
    const newPreviews = [...preview];
    newPreviews.splice(index, 1);
    setPreview(newPreviews);
  };
  
  const renderAttachments = (attachment) => {
    if (!attachment) return null;
    let urls = [];
    try {
      urls = JSON.parse(attachment);
      if (!Array.isArray(urls)) urls = [attachment];
    } catch (e) {
      urls = [attachment];
    }
    
    return (
      <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
        {urls.map((url, idx) => (
          <Button
            key={idx}
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon size={18} />}
            href={url}
            target="_blank"
            sx={{
              borderColor: "black",
              fontWeight: 500,
              color: "black !important",
              "& .MuiButton-startIcon": {
                color: "black !important",
              },
              "&:hover": {
                backgroundColor: "black !important",
                color: "white !important",
                borderColor: "black !important",
                "& .MuiSvgIcon-root": {
                  color: "white !important",
                },
              },
            }}
          >
            {urls.length > 1 ? `Attachment ${idx + 1}` : "Download Attachment"}
          </Button>
        ))}
      </Box>
    );
  };

  const handleCloseTicket = async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/tickets/close`,
        { ticket_id: ticketData.id, status: "Closed" },
        { withCredentials: true }
      );

      if (res.data.status === 200) {
        setState({
          ...state,
          open: true,
          severity: "success",
          message: "Ticket marked as Closed.",
        });

        setTimeout(() => {
          navigate("/tickets-list");
        }, 2000);
      }
    } catch (err) {
      console.error("Close Ticket Error:", err);
      setState({
        ...state,
        open: true,
        severity: "error",
        message: "Failed to close ticket",
      });
    }
  };

  const handleMouseEnter = () => {
    setRotate(360);
  };

  const handleMouseLeave = () => {
    setRotate(180);
  };

  useEffect(() => {
    if (meData && ticketData) {
      const isAdmin = meData.role_id === 1 || meData.role_id === 2;
      const isOwner = meData.role_id === 3 && meData.user_id === ticketData.created_user;

      if (!isAdmin && !isOwner) {
        setUnauthorized(true);
      }
    }
  }, [meData, ticketData]);

  if (unauthorized) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        flexDirection="column"
      >
        <Typography variant="h4" color="error">
          403 - Unauthorized
        </Typography>
        <Typography>You are not authorized to access this page.</Typography>
      </Box>
    );
  }

  return (
    <DashboardLayout>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={replyLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <MDBox mt={3} />
      <Card sx={{ p: 3 }}>
        {/* <Typography variant="h4" fontWeight="bold" gutterBottom>
          <Box component="span" mr={2}>
            #Ticket ID - {ticketData.id}
          </Box>
          <Box component="span" mr={2}>
            | {ticketData.user_name}
          </Box>
          <Box component="span">| {ticketData.name}</Box>
          {meData?.role_id === 3 && (
            <MDButton
              color="error"
              variant="contained"
              onClick={handleCloseTicket}
              sx={{ height: "40px" }}
            >
              Close Ticket
            </MDButton>
          )}
        </Typography> */}

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          {/* Left Side: Ticket Info */}
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            <Box component="span" mr={2}>
              #Ticket ID - {ticketData?.id}
            </Box>
            <Box component="span" mr={2}>
              | {ticketData?.user_name}
            </Box>
            <Box component="span">| {ticketData?.name}</Box>
          </Typography>

          {/* Right Side: Close Button */}
          {ticketStatus === "Closed"  ? (
            <MDButton
              color="error"
              variant="contained"
              disabled
              sx={{
                height: "40px",
                backgroundColor: "#b71c1c",
                cursor: "not-allowed",
              }}
            >
              Ticket Closed
            </MDButton>
          ) : (
            meData?.role_id === 3 && (
              <MDButton
                color="error"
                variant="contained"
                onClick={() => setShowConfirmDialog(true)}
                sx={{ height: "40px" }}
              >
                Close Ticket
              </MDButton>
            )
          )}
        </Box>

        {meData?.role_id === 3 && ticketStatus !== "Closed" && (
          <Typography variant="caption" color="error" sx={{ fontSize: "0.9rem", mt: 1 }}>
            (Note*: If not responded to the admin within 48 hours, the ticket will be auto-closed)
          </Typography>
        )}

        <Box mt={3} p={2} bgcolor="#f9f9f9" borderRadius={2}>
          <Typography variant="subtitle1" fontWeight={500}>
            {ticketData?.ticket_subject}
          </Typography>
          <Typography variant="body2" mt={1}>
            {ticketData?.ticket_description}
          </Typography>

          {ticketData?.ticket_attachment && (
            <Box display="flex" justifyContent="flex-end" mt={2}>
              {renderAttachments(ticketData.ticket_attachment)}
            </Box>
          )}
        </Box>

        {/* <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mt: 3,
            maxHeight: "60vh",
            overflowY: "auto",
            padding: 2,
            bgcolor: "#FDFDFD",
            borderRadius: 2,
          }}
        > */}
        {replyHistory.map((reply, index) => {
          const isMe = reply.created_by === meData?.user_id; // check if logged-in user
          return (
            <Box
              key={index}
              sx={{
                display: "flex",
                justifyContent: isMe ? "flex-end" : "flex-start",
                mt: 2,
                mb: 2,
              }}
            >
              <Box
                sx={{
                  maxWidth: "70%",
                  // bgcolor: isMe ? "#DCF8C6" : "#fff",
                  bgcolor: isMe ? "#E3F2FD" : "#F1F3F4",
                  borderRadius: 3,
                  px: 2,
                  py: 1.5,
                  boxShadow: 1,
                }}
              >
                {/* Username & time */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 0.5,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "#555" }}>
                    {reply.user_name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#888", fontSize: "0.7rem", ml: 1 }}>
                    {new Date(reply.created_at).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                </Box>

                {/* Message text */}
                <Typography variant="body2" sx={{ color: "#000" }}>
                  {reply.ticket_description}
                </Typography>

                {/* Attachment */}
                {reply.ticket_attachment && renderAttachments(reply.ticket_attachment)}
              </Box>
            </Box>
          );
        })}
        {/* </Box> */}

        {/* <form onSubmit={formik.handleSubmit}> */}
        {ticketStatus !== "Closed" && (
          <>
            <MDBox p={2}>
              <Grid container spacing={3}>
                <Grid item size={{ xs: 12, md: 12 }}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="txtContent"
                    value={formik.values.txtContent}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={Boolean(formik.touched.txtContent && formik.errors.txtContent)}
                    helperText={
                      formik.touched.txtContent && formik.errors.txtContent
                        ? formik.errors.txtContent
                        : `${formik.values.txtContent.length}/500`
                    }
                    multiline
                    rows={5}
                    inputProps={{ maxLength: 500 }}
                  />
                </Grid>

                <Grid item size={{ xs: 12, md: 12 }}>
                  <Typography sx={{ fontWeight: 500, mb: 1 }}>
                    Attach any supporting documents or images
                  </Typography>

                  <Box
                    p={2}
                    border="1px dashed"
                    borderColor="primary.main"
                    borderRadius={2}
                    bgcolor="#f9f9f9"
                    sx={{
                      maxWidth: 450,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: 1.5,
                      position: "relative",
                    }}
                  >
                    <input
                      type="file"
                      id="documents"
                      name="documents"
                      hidden
                      multiple
                      accept=".png,.jpg,.jpeg,.pdf"
                      onChange={handleFileChange}
                      onBlur={formik.handleBlur}
                    />
                    <label htmlFor="documents">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<IconUpload size={18} />}
                        component="span"
                        sx={{
                          borderColor: "black",
                          fontWeight: 500,
                          color: "black !important",
                          "& .MuiButton-startIcon": {
                            color: "black !important",
                          },
                          "& .MuiButton-label": {
                            color: "black !important",
                          },
                          "&:hover": {
                            backgroundColor: "black !important",
                            color: "white !important",
                            borderColor: "black !important",
                            "& .MuiSvgIcon-root": {
                              color: "white !important",
                            },
                            "& .MuiButton-startIcon": {
                              color: "white !important", // <-- icon turns white on hover
                              "& svg": {
                                color: "white !important",
                              },
                            },
                          },
                        }}
                      >
                        {formik.values.documents && formik.values.documents.length > 0 ? "Add More Files" : "Upload File"}
                      </Button>
                    </label>

                    {formik.values.documents && formik.values.documents.length > 0 && (
                      <Box sx={{ mt: 2, width: "100%" }}>
                        {formik.values.documents.map((file, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                              mt: 1,
                              p: 1,
                              bgcolor: "#fff",
                              borderRadius: 1,
                              border: "1px solid #eee",
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body2" sx={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {file.name}
                              </Typography>
                            </Box>
                            <IconButton
                              onClick={() => handleRemoveFile(index)}
                              size="small"
                              sx={{
                                p: 0.5,
                                backgroundColor: "#f5f5f5",
                                borderRadius: "50%",
                                transition: "0.3s ease",
                                "&:hover": {
                                  backgroundColor: "red",
                                  "& svg": {
                                    color: "#fff",
                                  },
                                },
                              }}
                            >
                              <IconX size={18} />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {preview && preview.length > 0 && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
                        {preview.map((p, index) => (
                          <Box key={index} sx={{ position: "relative" }}>
                            {p.url ? (
                              <img
                                src={p.url}
                                alt={`Preview ${index}`}
                                style={{
                                  width: 80,
                                  height: 80,
                                  borderRadius: 6,
                                  objectFit: "cover",
                                  border: "1px solid #eee",
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: 80,
                                  height: 80,
                                  borderRadius: 6,
                                  bgcolor: "#f0f0f0",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: "1px solid #eee",
                                }}
                              >
                                <IconFileDescription size={40} color="#888" />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: "0.6rem",
                                    textAlign: "center",
                                    px: 0.5,
                                    wordBreak: "break-all",
                                    color: "#666",
                                  }}
                                >
                                  {p.name.split(".").pop().toUpperCase()}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </MDBox>

            {/* Submit */}
            <MDBox p={2}>
              <Grid item size={{ xs: 12, md: 4 }}>
                <Grid item xs={12} md={12} textAlign="right">
                  <MDButton
                    // color="#2f2f34 !important"
                    onClick={formik.handleSubmit}
                    disabled={replyLoading}
                    sx={{
                      color: "white !important",
                      backgroundColor: "#16610E",
                      "&:hover": {
                        backgroundColor: "#1f1f22", // optional: darker on hover
                      },
                    }}
                  >
                    Reply
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>
          </>
        )}
        {/* </form> */}
      </Card>

      <Snackbar
        open={state.open}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleClose} severity={state.severity} variant="filled">
          {state.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiPaper-root": {
            borderRadius: 2,
            boxShadow: 6,
            position: "relative",
            overflow: "visible",
          },
        }}
      >
        <IconButton
          onClick={() => setShowConfirmDialog(false)}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{
            position: "absolute",
            top: -18,
            right: -16,
            backgroundColor: "#fff",
            color: "#ff0a54",
            boxShadow: 1,
            zIndex: 1500,
            p: 1.5,
            "&:hover": {
              backgroundColor: "#ff0a54",
              color: "#fff",
            },
            transition: "transform 0.5s ease",
            transform: `rotate(${rotate}deg)`,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle
          sx={{
            background: `linear-gradient(45deg, #800000, #191919)`,
            color: "white !important",
            fontWeight: "bold",
            fontSize: "18px",
            textAlign: "center",
            position: "relative",
            zIndex: 1400,
            py: 2,
          }}
        >
          Confirm Ticket Closure
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#000" }}>
            Are you sure you want to close this ticket? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowConfirmDialog(false);
              handleCloseTicket(); // 🔁 Call your actual close handler here
            }}
            variant="contained"
            size="small"
            sx={{
              backgroundColor: "#d00000",
              color: "#fff",
              transition: "transform 0.3s ease-in-out",
              "&:hover": {
                backgroundColor: "#9b0000",
                color: "#fff",
                fontWeight: "bold",
                transform: "scale(1.05)",
                boxShadow: "0 4px 10px rgba(0, 0, 0, 0.4)",
              },
            }}
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default TicketEdit;
